import * as React from "react"
import styled from "styled-components"
import { Product } from "../../interfaces/SaleorTypes"
import { useGetAccessoryQuery } from "../../../hooks-saleor/getAccessoryQuery"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import AccessorySlideCard from "../../molecules/store/AccessorySlideCard"
import Slider from "react-slick"
import { Hidden, Row, Visible } from "react-grid-system"
import CircleIndicator from "../../atoms/CircleIndicator"
import ArrowButton from "../../atoms/buttons/ArrowButton"
import { MediaQuery } from "../../../utils/design-helpers"
import { IOSArrowLeftIcon, IOSArrowRightIcon } from "../../atoms/icons/iOSArrowIcons"
import { AnimatePresence } from "framer-motion"
import Modal from "../../molecules/Modals/Modal"
import AccessoryModalContent from "../../molecules/Modals/AccessoryModalContent"
import ModalWithoutCard from "../../molecules/Modals/ModalWithoutCard"

interface BundleSuggestedSectionProps {
  onClickAddItem: (item: Product, number: number) => void
  gradient?: string
}

const SliderContainer = styled.div`
  position: relative;

  ${MediaQuery.XS} {
    margin-left: -16px;
  }
`

const LeftArrowMobile = styled.div`
  position: absolute;
  left: 4px;
  top: 50%;
  z-index: 3;
`

const RightArrowMobile = styled.div`
  position: absolute;
  right: -8px;
  top: 50%;
`

const Circles = styled.div<{ active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${(props) => (props.active ? props.theme.colors.greyDark : props.theme.colors.greyDisabled)};
  margin: 4px;
`

const BundleSuggestedSection: React.FunctionComponent<BundleSuggestedSectionProps> = ({
  onClickAddItem,
  gradient,
  ...props
}) => {
  const { accessories, loading, error } = useGetAccessoryQuery()
  const [currentSlide, setCurrentSlide] = React.useState(0)
  const sliderRef = React.useRef<any>(null)

  const [modalOpen, setModalOpen] = React.useState(false)

  const [currentAccessory, setCurrentAccessory] = React.useState<undefined | Product>(undefined)

  const onClickReadMore = (a: Product) => {
    setCurrentAccessory(a)
    setModalOpen(true)
  }

  const onClickAddToCart = (product: Product) => {
    onClickAddItem(product, 1)
  }

  const handleSliderAfterChange = (slideIndex: number) => {
    setCurrentSlide(slideIndex)
  }

  const gotoNext = () => {
    if (currentSlide !== accessories?.length) {
      sliderRef.current.slickNext()
    }
  }

  const gotoPrev = () => {
    if (currentSlide !== 0) {
      sliderRef.current.slickPrev()
    }
  }

  const [numberOfDots, setNumberOfDots] = React.useState(accessories?.length ?? 7 - 2)

  var settings = {
    infinite: false,
    speed: 500,
    afterChange: handleSliderAfterChange,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerPadding: "0.5rem",
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          centerMode: true,
          centerPadding: "2rem",
          slidesToScroll: 1,
          slidesToShow: 1,
        },
      },
    ],
  }

  const renderAccessories = () => {
    return accessories?.map((accessory: Product) => {
      return (
        <AccessorySlideCard
          key={accessory.id}
          product={accessory}
          onClickAddToCart={() => onClickAddToCart(accessory)}
          gradient={gradient}
          onClickReadMore={() => onClickReadMore(accessory)}
        />
      )
    })
  }

  return (
    <div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div>
          <Hidden xs>
            <div style={{ position: "relative" }}>
              <div style={{ position: "absolute", top: "50%", left: "5%" }}>
                <ArrowButton direction="l" onClick={gotoPrev} />
              </div>

              <div style={{ position: "absolute", top: "50%", right: "5%" }}>
                <ArrowButton direction="r" onClick={gotoNext} />
              </div>

              <div style={{ width: "80%", marginLeft: "10%" }}>
                <Slider ref={sliderRef} {...settings}>
                  {renderAccessories()}
                </Slider>
              </div>
            </div>
          </Hidden>

          <Visible xs>
            <SliderContainer>
              <LeftArrowMobile onClick={gotoPrev}>
                <IOSArrowLeftIcon />
              </LeftArrowMobile>

              <Slider ref={sliderRef} {...settings}>
                {renderAccessories()}
              </Slider>
              <Row justify="center">
                {accessories?.map((p: Product, index: number) => (
                  <div key={p.id} style={{ marginLeft: 4, marginRight: 4 }}>
                    <CircleIndicator isSelected={currentSlide === index} />
                  </div>
                ))}
              </Row>

              <RightArrowMobile onClick={gotoNext}>
                <IOSArrowRightIcon />
              </RightArrowMobile>
            </SliderContainer>
          </Visible>

          <Hidden xs>
            <Row justify="center" align="center">
              {Array.from({ length: numberOfDots }, (_, index) => (
                <Circles key={index} active={currentSlide === index} />
              ))}
            </Row>
          </Hidden>
        </div>
      )}

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {modalOpen && currentAccessory !== undefined && (
          <ModalWithoutCard open={modalOpen} onClose={() => setModalOpen(false)} closeIcon>
            <AccessoryModalContent accessory={currentAccessory} onClickAddItem={onClickAddItem} />
          </ModalWithoutCard>
        )}
      </AnimatePresence>

      <link
        rel="stylesheet"
        type="text/css"
        charSet="UTF-8"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
      />
      <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
      />
    </div>
  )
}

export default BundleSuggestedSection
